import React, { useState, useEffect } from 'react';
import Layout from '../../components/global/layout';
import ApiClient from '../../methods/api/apiClient';
import loader from '../../methods/loader';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { stripCallMeWhen } from '../../methods/callMeWhen';
import methodModel from '../../methods/methods';

const FindMember: React.FC = () => {
    const user = useSelector((state: any) => state.user);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<any[]>([]);
    const [searched, setSearched] = useState(false);
    const [recommendations, setRecommendations] = useState<any[]>([]);

    const groupId = user?.lastUsedGroupId?._id || user?.lastUsedGroupId;

    useEffect(() => {
        if (!groupId) return;
        ApiClient.get('api/profile/recommendations', { groupId }).then((res: any) => {
            if (res.success && res.data?.length) {
                setRecommendations(res.data);
            }
        });
    }, [groupId]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (!query.trim() || !groupId) return;

        loader(true);
        ApiClient.get('api/profile/search', { q: query, groupId, limit: 10 }).then((res: any) => {
            loader(false);
            setSearched(true);
            if (res.success) {
                setResults(res.data || []);
            } else {
                setResults([]);
            }
        });
    };

    return (
        <Layout>
            <div className="max-w-4xl mx-auto">
                <h3 className="mb-4">Find a Member</h3>
                <p className="text-gray-500 text-sm mb-4">
                    Search across all your group members by skills, services, industry, or any keyword.
                </p>

                {recommendations.length > 0 && (
                    <div className="mb-8">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Members Looking for Your Services</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {recommendations.map((member: any) => (
                                <div key={member._id} className="bg-white rounded-lg shadow-box p-4">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                                            {member.image ? (
                                                <img src={methodModel.userImg(member.image)} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm font-bold">
                                                    {(member.firstName || '?')[0].toUpperCase()}
                                                </div>
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            <Link
                                                to={`/user-profile/${member._id}`}
                                                className="font-medium text-blue-600 hover:underline text-sm block truncate"
                                            >
                                                {member.fullName || `${member.firstName || ''} ${member.lastName || ''}`.trim()}
                                            </Link>
                                            {member.profileData?.headline && (
                                                <p className="text-xs text-gray-500 truncate">{member.profileData.headline}</p>
                                            )}
                                        </div>
                                    </div>
                                    {member.matchingServices?.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                            {member.matchingServices.map((s: string, i: number) => (
                                                <span key={i} className="bg-green-50 text-green-700 text-xs px-2 py-0.5 rounded-full">
                                                    Needs {s}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <form onSubmit={handleSearch} className="flex gap-2 mb-6">
                    <input
                        type="text"
                        className="shadow-box bg-white flex-1 rounded-lg h-10 px-3 text-sm"
                        placeholder='Try "web developer", "real estate", "call me when you need marketing help"...'
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                    />
                    <button
                        type="submit"
                        className="text-white bg-orange-500 hover:bg-orange-600 font-medium rounded-lg text-sm px-6 h-10"
                    >
                        Search
                    </button>
                </form>

                {searched && results.length === 0 && (
                    <div className="text-center py-12 text-gray-400">
                        <p className="text-lg mb-2">No matching members found</p>
                        <p className="text-sm">Try different keywords or broader search terms</p>
                    </div>
                )}

                {results.length > 0 && (
                    <div className="space-y-3">
                        {results.map((member: any) => (
                            <div key={member._id} className="bg-white rounded-lg shadow-box p-4 flex items-start gap-4">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                                    {member.image ? (
                                        <img src={methodModel.userImg(member.image)} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-lg font-bold">
                                            {(member.firstName || '?')[0].toUpperCase()}
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <Link
                                            to={`/user-profile/${member._id}`}
                                            className="font-medium text-blue-600 hover:underline"
                                        >
                                            {member.fullName || `${member.firstName || ''} ${member.lastName || ''}`.trim()}
                                        </Link>
                                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                                            {Math.round(member.score * 100)}% match
                                        </span>
                                    </div>
                                    {member.profileData?.headline && (
                                        <p className="text-sm text-gray-600 mt-0.5">{member.profileData.headline}</p>
                                    )}
                                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                                        {member.profileData?.industry && <span>{member.profileData.industry}</span>}
                                        {member.company && <span>{member.company}</span>}
                                        {member.profession && <span>{member.profession}</span>}
                                    </div>
                                    {member.profileData?.specialties?.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {member.profileData.specialties.slice(0, 5).map((s: string, i: number) => (
                                                <span key={i} className="bg-orange-50 text-orange-700 text-xs px-2 py-0.5 rounded-full">
                                                    {s}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {stripCallMeWhen(member.profileData?.callMeWhen) && (
                                        <p className="text-xs text-gray-500 mt-1 italic">
                                            Call me when: {stripCallMeWhen(member.profileData?.callMeWhen)}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default FindMember;
